import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./HubSection.module.css";
import FiltersBar from "../components/FiltersBar";
import KpiCard from "../components/KpiCard";
import EvolutionChart from "../components/EvolutionChart";
import TopContentTable, { TopContentColumn, TopContentRow } from "../components/TopContentTable";
import DeeplinkSourcesList from "../components/DeeplinkSourcesList";
import NationalityList from "../components/NationalityList";
import ExportButton from "../components/ExportButton";
import { useHubAnalytics } from "../hooks/useHubAnalytics";
import { useFoldersForAnalytics } from "../hooks/useFoldersForAnalytics";
import { useExportCsv } from "../hooks/useExportCsv";
import { SelectOption } from "../types";
import { formatDecimal, formatInteger, formatPercent } from "../utils/formatNumber";
import { fillDailyGaps, startOfDay, endOfDay } from "../utils/deriveDateBuckets";

const PRIMARY = "#86e3ce";
const DARK = "#2bb8c8";
const HUB_MAX_DATE_RANGE_DAYS = 366;

interface HubSectionProps {
    isOwner: boolean;
}

const HubSection: React.FC<HubSectionProps> = ({ isOwner }) => {
    const { t } = useTranslation();
    const tt = t as unknown as (k: string) => string;

    const { folders, loading: foldersLoading } = useFoldersForAnalytics();
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    /** Snapshot of the (ids, range) used by the last successful fetch. Drives
     *  the chart's `fillDailyGaps`, AND is reused as the source of truth for
     *  the CSV export so the export can never desync from the displayed
     *  data (e.g. user clears the selection after fetching). */
    const [appliedQuery, setAppliedQuery] = useState<{
        ids: string[];
        start: Date;
        end: Date;
    } | null>(null);

    const { data, loading, fetch, reset } = useHubAnalytics();
    const { exporting, run: runExport } = useExportCsv();

    const companyOptions = useMemo<SelectOption[]>(() => {
        const byId = new Map<string, string>();
        for (const f of folders) {
            if (f.companyId) {
                byId.set(f.companyId, f.companyName || f.companyId);
            }
        }
        return Array.from(byId.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [folders]);

    const showCompanyFilter = isOwner && companyOptions.length > 1;

    const foldersForHubPicker = useMemo(() => {
        if (!isOwner || !showCompanyFilter) {
            return folders;
        }
        if (selectedCompanyIds.length === 0) {
            return folders;
        }
        return folders.filter((f) => f.companyId && selectedCompanyIds.includes(f.companyId));
    }, [folders, isOwner, showCompanyFilter, selectedCompanyIds]);

    const options = useMemo<SelectOption[]>(
        () =>
            foldersForHubPicker.map((f) => ({
                value: f.id,
                label: f.resolvedName || f.internalName,
                description: f.companyName || undefined,
            })),
        [foldersForHubPicker],
    );

    useEffect(() => {
        const allowed = new Set(foldersForHubPicker.map((f) => f.id));
        setSelectedFolderIds((ids) => ids.filter((id) => allowed.has(id)));
    }, [foldersForHubPicker]);

    const folderIdsResolvedForApply = useMemo(() => {
        if (selectedFolderIds.length > 0) {
            return Array.from(new Set(selectedFolderIds));
        }
        const ids = foldersForHubPicker.map((f) => f.id).filter(Boolean) as string[];
        return Array.from(new Set(ids));
    }, [selectedFolderIds, foldersForHubPicker]);

    const canApply = folderIdsResolvedForApply.length > 0 && Boolean(startDate) && Boolean(endDate);

    const handleApply = async () => {
        if (!canApply || !startDate || !endDate) return;
        const start = startOfDay(startDate);
        const end = endOfDay(endDate);
        const ids = folderIdsResolvedForApply.slice();
        await fetch({ folderIds: ids, startDate: start, endDate: end });
        setAppliedQuery({ ids, start, end });
    };

    const handleReset = () => {
        setSelectedCompanyIds([]);
        setSelectedFolderIds([]);
        setStartDate(null);
        setEndDate(null);
        setAppliedQuery(null);
        reset();
    };

    const handleExport = () => {
        if (!appliedQuery || appliedQuery.ids.length === 0) return;
        runExport({
            tab: "hubs",
            ids: appliedQuery.ids,
            startDate: appliedQuery.start,
            endDate: appliedQuery.end,
        });
    };

    const series = useMemo(() => {
        if (!data || !appliedQuery) return [];
        return fillDailyGaps(appliedQuery.start, appliedQuery.end, data.series, () => ({
            impressions: 0,
            uniqueVisitors: 0,
        }));
    }, [data, appliedQuery]);

    const topPagesColumns: TopContentColumn[] = [
        { key: "name", label: tt("page") },
        { key: "impressions", label: tt("impressions"), numeric: true },
        { key: "uniqueVisitors", label: tt("uniqueRow"), numeric: true },
    ];
    const topPagesRows: TopContentRow[] = useMemo(() => {
        if (!data) return [];
        return data.topTracks.map((t) => ({
            id: t.trackId,
            label: t.name,
            cells: { impressions: t.impressions, uniqueVisitors: t.uniqueVisitors },
        }));
    }, [data]);

    const appDeeplinks = data?.deeplinks?.app;
    const websiteDeeplinks = data?.deeplinks?.website;

    return (
        <div className={styles.root}>
            <FiltersBar
                selectorPlaceholder={tt("all_hubs")}
                options={options}
                selectedIds={selectedFolderIds}
                onSelectedIdsChange={setSelectedFolderIds}
                optionsLoading={foldersLoading}
                companyFilter={
                    showCompanyFilter
                        ? {
                              placeholder: tt("analyticsFilterCompanyPlaceholder"),
                              options: companyOptions,
                              selectedIds: selectedCompanyIds,
                              onSelectedIdsChange: setSelectedCompanyIds,
                              loading: foldersLoading,
                          }
                        : null
                }
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                maxRangeDays={HUB_MAX_DATE_RANGE_DAYS}
                canApply={canApply}
                applying={loading}
                onApply={handleApply}
                onReset={handleReset}
            />

            <div className={styles.headerRow}>
                <ExportButton
                    label={tt("exportCsv")}
                    loadingLabel={tt("exporting")}
                    onClick={handleExport}
                    disabled={!data || !appliedQuery || appliedQuery.ids.length === 0}
                    loading={exporting}
                />
            </div>

            <div className={styles.kpiGrid}>
                <KpiCard
                    title={tt("impressions")}
                    value={formatInteger(data?.impressions)}
                    help={tt("impressionsHelp")}
                />
                <KpiCard
                    title={tt("uniqueVisitors")}
                    value={formatInteger(data?.uniqueVisitors)}
                    help={tt("uniqueVisitorsHelp")}
                />
                <KpiCard
                    title={tt("appDeeplinkClicks")}
                    value={formatInteger(appDeeplinks?.totalClicks)}
                    help={tt("appDeeplinkClicksHelp")}
                />
                <KpiCard
                    title={tt("appDeeplinkConversion")}
                    value={
                        appDeeplinks?.conversionRate !== undefined ? formatPercent(appDeeplinks.conversionRate, 1) : "—"
                    }
                    help={tt("appDeeplinkConversionHelp")}
                />
            </div>

            <EvolutionChart
                title={tt("evolutionOfVisits")}
                subtitle={tt("evolutionOfVisitsSubtitle")}
                data={series}
                seriesDefs={[
                    { key: "impressions", label: tt("impressions"), color: PRIMARY },
                    { key: "uniqueVisitors", label: tt("visitors"), color: DARK },
                ]}
                emptyLabel={tt("noDataInRange")}
            />

            <div className={styles.twoCol}>
                <TopContentTable
                    title={tt("topStories")}
                    columns={topPagesColumns}
                    rows={topPagesRows}
                    emptyLabel={tt("noVisitsYet")}
                />
                <DeeplinkSourcesList
                    title={tt("appDeeplinkSources")}
                    subtitle={tt("appDeeplinkSourcesSubtitle")}
                    sources={appDeeplinks?.topSources || []}
                    conversionRate={appDeeplinks?.conversionRate || 0}
                    calloutLabel={tt("appDeeplinkGlobalConversion")}
                    clicksLabel={tt("clicksLabel")}
                    emptyLabel={tt("noDeeplinkClicks")}
                />
            </div>

            <section className={styles.secondaryBlock}>
                <header className={styles.secondaryHeader}>
                    <h3 className={styles.secondaryTitle}>{tt("websiteEngagement")}</h3>
                    <p className={styles.secondarySubtitle}>{tt("websiteEngagementSubtitle")}</p>
                </header>
                <div className={styles.kpiGrid}>
                    <KpiCard
                        title={tt("websiteClicks")}
                        value={formatInteger(websiteDeeplinks?.totalClicks)}
                        help={tt("websiteClicksHelp")}
                    />
                    <KpiCard
                        title={tt("websiteUniqueClickers")}
                        value={formatInteger(websiteDeeplinks?.uniqueClickers)}
                        help={tt("websiteUniqueClickersHelp")}
                    />
                    <KpiCard
                        title={tt("websiteConversion")}
                        value={
                            websiteDeeplinks?.conversionRate !== undefined
                                ? formatPercent(websiteDeeplinks.conversionRate, 1)
                                : "—"
                        }
                        help={tt("websiteConversionHelp")}
                    />
                </div>
                <DeeplinkSourcesList
                    title={tt("websiteSources")}
                    subtitle={tt("websiteSourcesSubtitle")}
                    sources={websiteDeeplinks?.topSources || []}
                    conversionRate={websiteDeeplinks?.conversionRate || 0}
                    calloutLabel={tt("websiteGlobalConversion")}
                    clicksLabel={tt("clicksLabel")}
                    emptyLabel={tt("noWebsiteClicks")}
                />
            </section>

            <NationalityList
                title={tt("distributionByNationality")}
                data={data?.byLanguage || []}
                emptyLabel={tt("noDataInRange")}
            />

            <div className={styles.kpiGrid}>
                <KpiCard
                    title={tt("contentImpressions")}
                    value={formatInteger(data?.contentImpressions)}
                    help={tt("contentImpressionsHelp")}
                />
                <KpiCard
                    title={tt("navigationImpressions")}
                    value={formatInteger(data?.navigationImpressions)}
                    help={tt("navigationImpressionsHelp")}
                />
                <KpiCard
                    title={tt("avgImpressionsPerVisitor")}
                    value={formatDecimal(data?.avgImpressionsPerVisitor, 1)}
                    help={tt("avgImpressionsPerVisitorHelp")}
                />
            </div>
        </div>
    );
};

export default HubSection;
